import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowLeft,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaMobileAlt,
  FaShieldAlt,
  FaDatabase,
  FaUserCog,
  FaQrcode,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../css/CutCutPage.css';

import screenLanding from '../assets/cutcut/landing.png';
import screenTestimonial from '../assets/cutcut/testimonial.png';
import screenDashboard from '../assets/cutcut/dashboard.png';
import screenServices from '../assets/cutcut/services.png';
import screenAdminDashboard from '../assets/cutcut/admin-dashboard.png';
import screenPayments from '../assets/cutcut/payments.png';
import cutcutQrCode from '../assets/cutcut/cutcut-testflight-qr.png';

const TESTFLIGHT_LINK = 'https://testflight.apple.com/join/MV1AuVHc';

const features = [
  {
    icon: <FaMobileAlt />,
    title: 'Premium Booking UX',
    text: 'Fast, clean barber booking flows with polished mobile-first design.'
  },
  {
    icon: <FaShieldAlt />,
    title: 'Secure Access',
    text: 'Protected user and admin flows with structured authentication.'
  },
  {
    icon: <FaDatabase />,
    title: 'Full-Stack System',
    text: 'Connected frontend, backend logic, payments, bookings, and dashboard data.'
  },
  {
    icon: <FaUserCog />,
    title: 'Admin Management',
    text: 'Control services, appointments, payments, notifications, and platform activity.'
  }
];

const screens = [
  {
    image: screenLanding,
    label: 'Landing',
    title: 'Premium customer-facing landing experience'
  },
  {
    image: screenTestimonial,
    label: 'Trust',
    title: 'Testimonial and conversion-focused flow'
  },
  {
    image: screenDashboard,
    label: 'User',
    title: 'Customer dashboard and quick actions'
  },
  {
    image: screenServices,
    label: 'Booking',
    title: 'Service selection and appointment flow'
  },
  {
    image: screenAdminDashboard,
    label: 'Admin',
    title: 'Admin modules and platform control'
  },
  {
    image: screenPayments,
    label: 'Payments',
    title: 'Revenue, payment status, and refund management'
  }
];

export default function CutCutPage() {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % screens.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + screens.length) % screens.length);
  };

  const activeScreen = screens[activeIndex];

  return (
    <div className="cc-page">
      <div className="cc-bg-orb orb-left" />
      <div className="cc-bg-orb orb-right" />
      <div className="cc-grid-overlay" />
      <div className="cc-watermark">CUTCUT BARBERSHOP</div>

      <div className="cc-shell">
        <motion.div
          className="cc-topbar"
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <Link to="/" className="cc-back-link">
            <FaArrowLeft />
            Back to Home
          </Link>

          <a
            href={TESTFLIGHT_LINK}
            target="_blank"
            rel="noreferrer"
            className="cc-testflight-link-top"
          >
            TestFlight
            <FaExternalLinkAlt />
          </a>
        </motion.div>

        <div className="cc-hero-layout">
          <motion.div
            className="cc-left"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="cc-tag">Case Study / Product Page</span>
            <h1>CutCut Barbershop App</h1>
            <p className="cc-lead">
              CutCut Barbershop is a premium booking platform for modern grooming
              businesses. It combines customer booking journeys, account flows,
              admin dashboards, payment visibility, and a polished mobile-first
              interface into one strong product experience.
            </p>

            <div className="cc-feature-grid">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="cc-feature-card"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 + index * 0.06, duration: 0.35 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="cc-feature-icon">{feature.icon}</div>
                  <div>
                    <h4>{feature.title}</h4>
                    <p>{feature.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="cc-stat-row">
              <div className="cc-stat-box">
                <strong>2</strong>
                <span>Customer + Admin</span>
              </div>
              <div className="cc-stat-box">
                <strong>6</strong>
                <span>Core Screens</span>
              </div>
              <div className="cc-stat-box">
                <strong>Booking</strong>
                <span>Payments + Control</span>
              </div>
            </div>

            <div className="cc-cta-row">
              <a
                href={TESTFLIGHT_LINK}
                target="_blank"
                rel="noreferrer"
                className="cc-btn cc-btn-primary"
              >
                Join TestFlight
              </a>

              <Link to="/#projects" className="cc-btn cc-btn-secondary">
                More Projects
              </Link>
            </div>
          </motion.div>

          <motion.div
            className="cc-right"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.75 }}
          >
            <div className="cc-screen-panel">
              <div className="cc-screen-topbar">
                <div className="cc-screen-meta">
                  <span className="cc-screen-label">{activeScreen.label}</span>
                  <h3>{activeScreen.title}</h3>
                </div>

                <div className="cc-screen-controls">
                  <button type="button" onClick={prevSlide} aria-label="Previous screen">
                    <FaChevronLeft />
                  </button>
                  <button type="button" onClick={nextSlide} aria-label="Next screen">
                    <FaChevronRight />
                  </button>
                </div>
              </div>

              <div className="cc-phone-stage">
                <motion.div
                  className="cc-phone-card cc-phone-back left"
                  animate={{ y: [0, -8, 0], rotate: [-8, -10, -8] }}
                  transition={{ repeat: Infinity, duration: 5.6, ease: 'easeInOut' }}
                >
                  <img
                    src={screens[(activeIndex + 1) % screens.length].image}
                    alt="CutCut preview background left"
                  />
                </motion.div>

                <motion.div
                  className="cc-phone-card cc-phone-back right"
                  animate={{ y: [0, -10, 0], rotate: [8, 10, 8] }}
                  transition={{ repeat: Infinity, duration: 5.9, ease: 'easeInOut' }}
                >
                  <img
                    src={screens[(activeIndex + 2) % screens.length].image}
                    alt="CutCut preview background right"
                  />
                </motion.div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    className="cc-phone-card cc-phone-main"
                    initial={{ opacity: 0, y: 30, scale: 0.94, rotate: -2 }}
                    animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
                    exit={{ opacity: 0, y: -18, scale: 0.96, rotate: 2 }}
                    transition={{ duration: 0.45 }}
                  >
                    <div className="cc-phone-notch" />
                    <img
                      src={activeScreen.image}
                      alt={activeScreen.title}
                      className="cc-phone-image"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="cc-screen-thumbs">
                {screens.map((screen, index) => (
                  <button
                    key={screen.label}
                    type="button"
                    className={`cc-thumb ${activeIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Show ${screen.label}`}
                  >
                    <img src={screen.image} alt={screen.label} />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="cc-qr-section"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <div className="cc-qr-copy">
            <span className="cc-qr-chip">
              <FaQrcode />
              TestFlight QR
            </span>
            <h2>Scan to join the CutCut beta</h2>
            <p>
              Open the CutCut Barbershop TestFlight invite directly on your
              iPhone and access the beta faster.
            </p>

            <a
              href={TESTFLIGHT_LINK}
              target="_blank"
              rel="noreferrer"
              className="cc-testflight-text-link"
            >
              Open TestFlight <FaArrowRight />
            </a>
          </div>

          <a
            href={TESTFLIGHT_LINK}
            target="_blank"
            rel="noreferrer"
            className="cc-qr-card"
            aria-label="Open CutCut TestFlight"
          >
            <img
              src={cutcutQrCode}
              alt="CutCut Barbershop TestFlight QR"
              className="cc-qr-image"
            />
          </a>
        </motion.div>
      </div>
    </div>
  );
}